import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getCategoriesForStage, getEventDetail, getResults } from "../lib/api";
import type { RaceDayCategory, RaceDayEventDetail, RaceDayResults } from "../lib/types";
import { useWatchList } from "../lib/useWatchList";
import ResultsTable from "../components/ResultsTable";
import StatusPill from "../components/StatusPill";
import CopyLinkButton from "../components/CopyLinkButton";

const POLL_VISIBLE_MS = 30_000;
const POLL_HIDDEN_MS = 120_000;

export default function CategoryResults() {
  const { eventId, catId } = useParams<{ eventId: string; catId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const stageId = searchParams.get("s");
  const spotlight = searchParams.get("rider") || undefined;

  const [event, setEvent] = useState<RaceDayEventDetail | null>(null);
  const [results, setResults] = useState<RaceDayResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const watch = useWatchList();

  // Resolve event detail once so we can name the category and find a default stage.
  useEffect(() => {
    let cancelled = false;
    if (!eventId) return;
    getEventDetail(eventId)
      .then((res) => {
        if (cancelled) return;
        setEvent(res.event);
        if (!stageId) {
          const sid = res.event.defaultStageId ?? res.event.stages[0]?.id;
          if (sid) {
            const next = new URLSearchParams(searchParams);
            next.set("s", sid);
            setSearchParams(next, { replace: true });
          }
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load event");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const activeStageId = stageId ?? event?.defaultStageId ?? event?.stages[0]?.id;

  const latestResultsRef = useRef<RaceDayResults | null>(null);
  useEffect(() => {
    if (!activeStageId || !catId) return;
    let cancelled = false;
    setResults(null);

    let timer: number | undefined;

    const load = async () => {
      try {
        const { results } = await getResults({
          stageId: activeStageId,
          categoryId: catId,
        });
        if (!cancelled) {
          latestResultsRef.current = results;
          setResults(results);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load results");
        }
      }
    };

    const schedule = () => {
      const latest = latestResultsRef.current;
      const final = latest && /final/i.test(latest.status);
      if (final) return;
      const visible = document.visibilityState === "visible";
      timer = window.setTimeout(
        () => {
          load().then(schedule);
        },
        visible ? POLL_VISIBLE_MS : POLL_HIDDEN_MS,
      );
    };

    load().then(schedule);

    const onVis = () => {
      if (timer) {
        clearTimeout(timer);
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStageId, catId]);

  // Categories for the *active* stage. When viewing the default stage we already
  // have these in event.categories; otherwise fetch via /get_cats_for_stage.
  const [activeStageCategories, setActiveStageCategories] = useState<
    RaceDayCategory[] | null
  >(null);
  useEffect(() => {
    if (!event || !activeStageId) return;
    if (activeStageId === event.defaultStageId) {
      setActiveStageCategories(event.categories);
      return;
    }
    let cancelled = false;
    setActiveStageCategories(null);
    getCategoriesForStage({ eventId: event.id, stageId: activeStageId })
      .then((res) => {
        if (!cancelled) setActiveStageCategories(res.categories);
      })
      .catch(() => {
        /* fall back: leave null, name lookup will degrade gracefully */
      });
    return () => {
      cancelled = true;
    };
  }, [event, activeStageId]);

  const category = activeStageCategories?.find((c) => c.id === catId);
  const stage = event?.stages.find((s) => s.id === activeStageId);

  const navigate = useNavigate();
  const [switchingStage, setSwitchingStage] = useState<string | null>(null);
  const switchToStage = async (newStageId: string) => {
    if (!event || !category) return;
    if (newStageId === activeStageId) return;
    setSwitchingStage(newStageId);
    try {
      const { categories } = await getCategoriesForStage({
        eventId: event.id,
        stageId: newStageId,
      });
      const target = categories.find(
        (c) => c.name.trim().toLowerCase() === category.name.trim().toLowerCase(),
      );
      if (!target) {
        // Category doesn't exist in the new stage. Send the user to the event
        // page for that stage so they can pick from what's available.
        navigate(`/event/${event.id}?s=${newStageId}`);
        return;
      }
      navigate(`/event/${event.id}/c/${target.id}?s=${newStageId}`);
    } catch {
      /* leave UI as-is */
    } finally {
      setSwitchingStage(null);
    }
  };

  const refresh = async () => {
    if (!activeStageId || !catId) return;
    setRefreshing(true);
    try {
      const { results } = await getResults({
        stageId: activeStageId,
        categoryId: catId,
        cacheBust: true,
      });
      setResults(results);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  if (error && !results) {
    return <div className="text-sm text-pit-accent font-mono">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/event/${eventId}`}
          className="text-xs font-mono text-pit-muted hover:text-pit-text"
        >
          ← {event?.name ?? "event"}
        </Link>
        <div className="flex items-start justify-between gap-3 mt-1">
          <div>
            <h1 className="text-xl font-mono font-bold">
              {category?.name ?? "Results"}
            </h1>
            <div className="text-sm text-pit-muted font-mono mt-0.5">
              {event?.name}
              {event?.date && ` · ${event.date}`}
              {stage && event && event.stages.length > 1 && ` · ${stage.name}`}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            <button onClick={refresh} className="btn">
              {refreshing ? "↻" : "refresh"}
            </button>
            <CopyLinkButton />
          </div>
        </div>
      </div>

      {event && event.stages.length > 1 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-pit-muted font-mono">
            Stage
          </div>
          <div className="flex flex-wrap gap-1.5">
            {event.stages.map((s) => {
              const active = s.id === activeStageId;
              const busy = switchingStage === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => switchToStage(s.id)}
                  disabled={busy || !category}
                  className={`btn ${active ? "btn-primary" : ""} ${busy ? "opacity-60" : ""}`}
                >
                  {s.name}
                  {busy ? " ↻" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {results && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <StatusPill status={results.status} updatedAt={results.statusUpdatedAt} />
          <div className="text-[11px] font-mono text-pit-muted">
            {results.rows.length} {results.rows.length === 1 ? "rider" : "riders"} ·{" "}
            {results.resultsType}
          </div>
        </div>
      )}

      {!results && <div className="text-sm text-pit-muted font-mono">Loading results…</div>}

      {results && (
        <ResultsTable
          results={results}
          watchedRiders={watch.riders}
          spotlight={spotlight}
        />
      )}
    </div>
  );
}
